import React, { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Modal, Form, Button, ButtonGroup, Badge, Pagination, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FiEye, FiPrinter, FiEdit, FiTrash2, FiCalendar, FiList, FiPlus, FiClock, FiUser, FiPhone, FiMail, FiMapPin, FiFileText, FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import { FaPrint } from "react-icons/fa";
import { GiDogBowl } from "react-icons/gi";
import { MdColorLens } from "react-icons/md";
import { FaBirthdayCake } from "react-icons/fa";
import Swal from 'sweetalert2';

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

function formatDateTime(dateString) {
    const date = new Date(dateString);

    return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
}

// Status options
const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: '#ffc107', bg: 'warning' },
    { value: 'scheduled', label: 'Scheduled', color: '#0d6efd', bg: 'primary' },
    { value: 'in_progress', label: 'In Progress', color: '#0dcaf0', bg: 'info' },
    { value: 'completed', label: 'Completed', color: '#198754', bg: 'success' },
    { value: 'rescheduled', label: 'Rescheduled', color: '#fd7e14', bg: 'warning' },
    { value: 'missed', label: 'Missed', color: '#6c757d', bg: 'secondary' },
    { value: 'cancelled', label: 'Cancelled', color: '#dc3545', bg: 'danger' }
];

// Status Badge Component - Without Dropdown (using buttons instead)
const StatusBadge = ({ status, onStatusChange, appointmentId }) => {
    const [updating, setUpdating] = useState(false);
    const [showStatusOptions, setShowStatusOptions] = useState(false);
    const currentStatus = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

    const handleStatusChange = async (newStatus) => {
        if (newStatus === status) {
            setShowStatusOptions(false);
            return;
        }

        setUpdating(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/appointments-change-status/${appointmentId}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                onStatusChange(appointmentId, newStatus);
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Status updated successfully',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update status',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setUpdating(false);
            setShowStatusOptions(false);
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <Button
                variant={currentStatus.bg}
                size="sm"
                disabled={updating}
                onClick={() => setShowStatusOptions(!showStatusOptions)}
                className="d-inline-flex align-items-center gap-1"
            >
                {currentStatus.label}
                <FiChevronDown size={12} />
                {updating && <span className="spinner-border spinner-border-sm ms-1" />}
            </Button>

            {showStatusOptions && (
                <div className="status-options-popup" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    minWidth: '120px',
                    marginTop: '4px'
                }}>
                    {STATUS_OPTIONS.map(option => (
                        <Button
                            key={option.value}
                            variant="light"
                            size="sm"
                            onClick={() => handleStatusChange(option.value)}
                            className="d-block w-100 text-start border-0"
                            style={{
                                backgroundColor: status === option.value ? option.color : 'transparent',
                                color: status === option.value ? 'white' : 'inherit'
                            }}
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
};

const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

const hasPermission = (perm) => {
    return permissions.includes(perm);
};

const role = localStorage.getItem("role");
const isAdmin = role?.toLowerCase() === 'Admin';

// Action Buttons Component - Without Dropdown (using simple buttons)
const ActionButtons = ({ appointment, onView, onEdit, onDelete, onInvoicePrint, onPrescriptionPrint }) => {
    const { t } = useContext(TranslatorContext);

    return (
        <div className="d-flex gap-2 justify-content-center">
            <Button
                variant="info"
                size="sm"
                onClick={() => onEdit(appointment.id)}
                title={t("Details")}
            >
                <FiEdit size={14} />
            </Button>
            {hasPermission("Appointments edit") && (
                <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onDelete(appointment.id)}
                    title={t("Delete")}
                >
                    <FiTrash2 size={14} />
                </Button>
            )}
            <Button
                variant="secondary"
                size="sm"
                onClick={() => onInvoicePrint(appointment.id)}
                title={t("Invoice Print")}
            >
                <FiFileText size={14} />
            </Button>
            <Button
                variant="primary"
                size="sm"
                onClick={() => onPrescriptionPrint(appointment.id)}
                title={t("Print Prescription")}
            >
                <FaPrint size={14} />
            </Button>
            {/* <Button
                variant="danger"
                size="sm"
                onClick={() => onDelete(appointment.id)}
                title={t("Delete")}
            >
                <FiTrash2 size={14} />
            </Button> */}
        </div>
    );
};

// Main Appointment Index Page
export default function BookedAppointmentIndexPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const AuthToken = localStorage.getItem("authToken");

    const [appointments, setAppointments] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [pets, setPets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewType, setViewType] = useState('table');
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingAppointment, setViewingAppointment] = useState(null);

    const today = new Date().toISOString().split("T")[0];

    // Filter states
    const [tempFilters, setTempFilters] = useState({
        doctor_id: '',
        customer_id: '',
        pet_id: '',
        date: today,
        start_date: today,
        end_date: today,
        per_page: 10
    });

    const [activeFilters, setActiveFilters] = useState({
        doctor_id: '',
        customer_id: '',
        pet_id: '',
        date: today,
        start_date: today,
        end_date: today,
        per_page: 10
    });

    // Pagination states
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    useEffect(() => {
        fetchAppointments();
        fetchFilterData();
    }, [activeFilters.doctor_id, activeFilters.customer_id, activeFilters.pet_id, activeFilters.date, activeFilters.start_date, activeFilters.end_date, activeFilters.per_page, pagination.current_page]);

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.current_page,
                per_page: activeFilters.per_page,
                ...(activeFilters.doctor_id && { doctor_id: activeFilters.doctor_id }),
                ...(activeFilters.customer_id && { customer_id: activeFilters.customer_id }),
                ...(activeFilters.pet_id && { pet_id: activeFilters.pet_id }),
                ...(activeFilters.date && { date: activeFilters.date }),
                ...(activeFilters.start_date && { start_date: activeFilters.start_date }),
                ...(activeFilters.end_date && { end_date: activeFilters.end_date })
            });

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/booked/appointments?${params}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            setAppointments(response.data.appointments.data);
            setPagination({
                current_page: response.data.appointments.current_page || 1,
                last_page: response.data.appointments.last_page || 1,
                per_page: response.data.appointments.per_page || 10,
                total: response.data.appointments.total || 0,
                from: response.data.appointments.from || 0,
                to: response.data.appointments.to || 0
            });
        } catch (err) {
            console.error('Error fetching appointments:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch appointments',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchFilterData = async () => {
        try {
            const doctorsRes = await axios.get(`${process.env.REACT_APP_API_URL}/doctors`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            setDoctors(doctorsRes.data.doctors.data || []);

            const customersRes = await axios.get(`${process.env.REACT_APP_API_URL}/customers`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            setCustomers(customersRes.data.customers.data || []);

            const petsRes = await axios.get(`${process.env.REACT_APP_API_URL}/pets?per_page=1000`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            if (Array.isArray(petsRes.data.pets)) {
                setPets(petsRes.data.pets);
            } else if (petsRes.data.pets && petsRes.data.pets.data) {
                setPets(petsRes.data.pets.data);
            } else {
                setPets([]);
            }
        } catch (err) {
            console.error('Error fetching filter data:', err);
            setDoctors([]);
            setCustomers([]);
            setPets([]);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applySearch = () => {
        setActiveFilters({
            doctor_id: tempFilters.doctor_id,
            customer_id: tempFilters.customer_id,
            pet_id: tempFilters.pet_id,
            date: tempFilters.date,
            start_date: tempFilters.start_date,
            end_date: tempFilters.end_date,
            per_page: tempFilters.per_page
        });
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    const clearFilters = () => {
        const emptyFilters = {
            doctor_id: '',
            customer_id: '',
            pet_id: '',
            date: '',
            start_date: '',
            end_date: '',
            per_page: 10
        };
        setTempFilters(emptyFilters);
        setActiveFilters(emptyFilters);
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setPagination(prev => ({
                ...prev,
                current_page: page
            }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePerPageChange = (e) => {
        const value = parseInt(e.target.value);
        setTempFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setActiveFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    const handleStatusUpdate = (appointmentId, newStatus) => {
        setAppointments(prevAppointments =>
            prevAppointments.map(appointment =>
                appointment.id === appointmentId
                    ? { ...appointment, status: newStatus }
                    : appointment
            )
        );

        if (viewingAppointment && viewingAppointment.id === appointmentId) {
            setViewingAppointment({ ...viewingAppointment, status: newStatus });
        }
    };

    const handleViewClick = (appointment) => {
        setViewingAppointment(appointment);
        setShowViewModal(true);
    };

    const handleEdit = (id) => {
        navigate(`/appointments/${id}`);
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${process.env.REACT_APP_API_URL}/deleteAppointment/${id}`, {
                        headers: { Authorization: `Bearer ${AuthToken}` }
                    });

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: t('Appointment deleted successfully!'),
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 2000
                    });

                    fetchAppointments();
                } catch (err) {
                    console.error(err);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: t('Failed to delete appointment'),
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 2000
                    });
                }
            }
        });
    };

    const handleInvoicePrint = (id) => {
        window.open(`${process.env.REACT_APP_URL}/invoice-print/${id}`, "_blank");
    };

    const handlePrescriptionPrint = (id) => {
        window.open(`${process.env.REACT_APP_URL}/appointment-print/${id}`, "_blank");
    };

    const renderBootstrapPagination = () => {
        let items = [];
        let maxVisible = 5;
        let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
        let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    {1}
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.current_page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.last_page) {
            if (endPage < pagination.last_page - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.last_page}
                    onClick={() => handlePageChange(pagination.last_page)}
                >
                    {pagination.last_page}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
            />
        );

        return items;
    };

    const parseTimeToISO = (timeStr) => {
        if (!timeStr) return '10:00:00';

        try {
            const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
            if (match) {
                let hour = parseInt(match[1]);
                const minute = match[2];
                const ampm = match[3].toUpperCase();

                if (ampm === 'PM' && hour !== 12) hour += 12;
                if (ampm === 'AM' && hour === 12) hour = 0;

                return `${hour.toString().padStart(2, '0')}:${minute}:00`;
            }
        } catch (e) {
            console.error('Error parsing time:', e);
        }
        return '10:00:00';
    };

    const handleEventClick = (info) => {
        const appointment = info.event.extendedProps.appointment;
        handleViewClick(appointment);
    };

    return (
        <PageLayout>
            <div className="appointment-index-page">
                {/* Sticky Header */}
                <div className="sticky-header p-3 bg-white shadow-sm mb-4 rounded">
                    <Row className="align-items-center">
                        <Col md={6}>
                            <h2 className="mb-0 fw-bold">
                                <FiCalendar className="me-2 text-primary" />
                                {t("Appointments")}
                            </h2>
                            <p className="text-muted mt-2 mb-0">Manage all Web booking appointments</p>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-end gap-3">

                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Filters Section */}
                <Card className="shadow-sm mb-4">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0"><FiSearch className="me-2" /> {t("Filters")}</h6>
                            <div className="d-flex gap-2">
                                <Button variant="primary" size="sm" onClick={applySearch}>
                                    <FiSearch className="me-1" /> {t("Search")}
                                </Button>
                                <Button variant="danger" size="sm" onClick={clearFilters}>
                                    <FiX className="me-1" /> {t("Clear All")}
                                </Button>
                            </div>
                        </div>
                        <Row>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("ExDoctor")}</Form.Label>
                                    <Form.Select
                                        name="doctor_id"
                                        size="sm"
                                        value={tempFilters.doctor_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t("All Doctors")}</option>
                                        {doctors.map(doctor => (
                                            <option key={doctor.id} value={doctor.id}>
                                                Dr. {doctor.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Customer")}</Form.Label>
                                    <Form.Select
                                        name="customer_id"
                                        size="sm"
                                        value={tempFilters.customer_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t("All Customers")}</option>
                                        {customers.map(customer => (
                                            <option key={customer.id} value={customer.id}>
                                                {customer.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Pet")}</Form.Label>
                                    <Form.Select
                                        name="pet_id"
                                        size="sm"
                                        value={tempFilters.pet_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t("All Pets")}</option>
                                        {Array.isArray(pets) && pets.map(pet => (
                                            <option key={pet.id} value={pet.id}>
                                                {pet.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Per Page")}</Form.Label>
                                    <Form.Select
                                        name="per_page"
                                        size="sm"
                                        value={tempFilters.per_page}
                                        onChange={handlePerPageChange}
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Single Date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="date"
                                        size="sm"
                                        value={tempFilters.date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Start Date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="start_date"
                                        size="sm"
                                        value={tempFilters.start_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("End Date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="end_date"
                                        size="sm"
                                        value={tempFilters.end_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {viewType === 'table' ? (
                            <>
                                <Card className="shadow-sm">
                                    <Card.Body className="p-0">
                                        <div className="table-responsive-wrapper">
                                            <Table responsive hover className="table mb-0">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '15%' }}>{t("Appointment Number")}</th>
                                                        <th style={{ width: '10%' }}>{t("Pet Name")}</th>
                                                        <th style={{ width: '12%' }}>{t("Owner")}</th>
                                                        <th style={{ width: '10%' }}>{t("Date")}</th>
                                                        <th style={{ width: '10%' }}>{t("Time")}</th>
                                                        <th style={{ width: '15%' }}>{t("Expected Date")}</th>
                                                        <th style={{ width: '13%' }}>{t("Status")}</th>
                                                        <th style={{ width: '15%' }} className="text-center">{t("Actions")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {appointments.length > 0 ? (
                                                        appointments.map((appointment) => (
                                                            <tr key={appointment.id}>
                                                                <td>
                                                                    <strong className="text-primary">
                                                                        {appointment.appointment_number || `APT-${String(appointment.id).padStart(5, '0')}`}
                                                                    </strong>
                                                                </td>
                                                                <td>{appointment.pet?.name || '—'}</td>
                                                                <td>{appointment.customer?.name || '—'}</td>
                                                                <td>{formatDate(appointment.appointment_date)}</td>
                                                                <td>
                                                                    <Badge bg="secondary" className="px-2 py-1">
                                                                        {appointment.appointment_time_slot || '—'}
                                                                    </Badge>
                                                                </td>
                                                                <td>{formatDateTime(appointment.appointment_expected_date)}</td>
                                                                <td>
                                                                    <StatusBadge
                                                                        status={appointment.status}
                                                                        onStatusChange={handleStatusUpdate}
                                                                        appointmentId={appointment.id}
                                                                    />
                                                                </td>
                                                                <td className="text-center">
                                                                    <ActionButtons
                                                                        appointment={appointment}
                                                                        onView={handleViewClick}
                                                                        onEdit={handleEdit}
                                                                        onDelete={handleDelete}
                                                                        onInvoicePrint={handleInvoicePrint}
                                                                        onPrescriptionPrint={handlePrescriptionPrint}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="8" className="text-center py-5">
                                                                <div className="empty-state">
                                                                    <FiCalendar size={48} className="text-muted mb-2" />
                                                                    <p className="text-muted mb-0">{t("No appointments found")}</p>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>

                                {/* Pagination Section */}
                                {pagination.last_page > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-2">
                                        <div className="text-muted small">
                                            {t('Showing')} {pagination.from} {t('to')} {pagination.to} {t('of')} {pagination.total} {t('entries')}
                                        </div>
                                        <Pagination className="mb-0">
                                            {renderBootstrapPagination()}
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        ) : (
                            <Card className="shadow-sm">
                            </Card>
                        )}
                    </>
                )}
            </div>
        </PageLayout>
    );
}